import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import ProductForm from "./ProductForm";
import { productToFormValues } from "./product-form-values";
import { useProductTypeLookup, useMaterialTypeLookup, useProcessLookup } from "@/hooks/useFKLookup";
import type { ProductFormData } from "../../../shared/schemas/product.schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { ModalBreadcrumbHeader } from "@/components/shared/ModalBreadcrumbHeader";

interface ProductEditModalProps {
  productId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (updatedProduct?: any) => void;
  breadcrumb?: string[];
}

export default function ProductEditModal({
  productId,
  isOpen,
  onClose,
  onSuccess,
  breadcrumb = ["Sản phẩm", "Chỉnh sửa sản phẩm"],
}: ProductEditModalProps) {
  const queryClient = useQueryClient();
  const { options: productTypeOptions } = useProductTypeLookup();
  const { options: materialTypeOptions } = useMaterialTypeLookup();
  const { options: processOptions } = useProcessLookup();

  const { data: productData, isLoading } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      return trpcClient.products.getById.query({ id: productId });
    },
    enabled: isOpen && !!productId,
  });

  const handleSubmit = async (data: ProductFormData) => {
    try {
      const updated = await trpcClient.products.update.mutate({
        id: productId,
        data: data as any,
      });

      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["product", productId] });
      await queryClient.invalidateQueries({ queryKey: ["entity-select", "products"] });
      await queryClient.invalidateQueries({ queryKey: ["combobox"] });

      toast.success("Cập nhật sản phẩm thành công");
      onSuccess?.(updated);
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Đã có lỗi xảy ra");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="w-[96vw] sm:max-w-4xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="p-0 mb-2">
          <ModalBreadcrumbHeader
            breadcrumbs={breadcrumb}
            title="Chỉnh sửa sản phẩm bao bì"
            description="Cập nhật quy cách, kích thước, cấu trúc hộp và công đoạn sản xuất."
          />
          <DialogDescription className="sr-only">Biểu mẫu cập nhật thông tin sản phẩm</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-4 py-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : productData ? (
          <ProductForm
            // Cùng cách nạp với trang Sửa sản phẩm: công đoạn / vật tư theo thứ tự đã lưu (FB-4T1R48)
            defaultValues={productToFormValues(productData, { productTypeOptions, materialTypeOptions, processOptions }) as any}
            onSubmit={handleSubmit}
            onCancel={onClose}
            breadcrumb={[...breadcrumb, "Biểu mẫu"]}
          />
        ) : (
          <div className="py-6 text-center text-slate-500">Không tìm thấy dữ liệu</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
